import get from 'lodash/get';
import omit from 'lodash/omit';
import isBoolean from 'lodash/isBoolean';
import {clone, keyMirror} from 'utils/DBUtils';

export const Types = keyMirror({
    CREATE_RESOURCE: null,
    CREATED_RESOURCE: null,
    SYNCHRONIZE_RESOURCE: null,
    SYNCHRONIZED_RESOURCE: null,
    DELETE_RESOURCE: null,
    DELETED_RESOURCE: null,
    SAVE_RESOURCE_FIELD: null,
    SAVE_RESOURCE_CHANGELIST_SUBMIT: null,
    SAVE_RESOURCE_CHANGELIST_SUCCESS: null,
    SAVE_RESOURCE_CHANGELIST_ERROR: null,
    SENT_SAVE_RESOURCE_FIELD: null,
    RESOURCE_FIELD_SAVED: null,
    SAVE_RESOURCE_FIELD_ERROR: null,
    GET_RESOURCE_PAGE: null,
    RECEIVED_RESOURCE_PAGE: null,
    CLEAR_RESOURCE_PAGE: null,
    GET_RESOURCE: null,
    RECEIVED_RESOURCE: null,
    GET_ALL_RESOURCES: null,
    RECEIVED_ALL_RESOURCES: null,
    UPDATE_PAGE_NUMBER_FOR_RESOURCE: null,
    UPDATE_PAGE_COUNT_FOR_RESOURCE: null,
    TOGGLE_SORT_FOR_RESOURCE: null,
    ADD_FILTER_FOR_RESOURCE: null,
    REMOVE_FILTER_FOR_RESOURCE: null,
    REMOVE_ALL_FILTERS_FOR_RESOURCE: null,
    REFRESH_PAGE: null,
    CLEAR_SAVE_ERRORS: null,
    GET_RESOURCE_INFO: null,
    RECEIVED_RESOURCE_INFO: null,
});

export const FieldSavedResourceActionFor = (resourceType = null) =>
    `${Types.RESOURCE_FIELD_SAVED}${resourceType ? `_${resourceType.toUpperCase()}` : ''}`;

export const createResourceAction = (type, data, rootResource, resourceIds, resourcePath, fieldPath) => ({
    type: Types.CREATE_RESOURCE,
    resourceType: type,
    data: data,
    rootResource: rootResource,
    resourceIds: resourceIds,
    resourcePath: resourcePath,
    fieldPath: fieldPath
});

export const createdResourceAction = (resource, rootResource, resourceIds, resourcePath, fieldPath, modifiedBreak) => ({
    type: Types.CREATED_RESOURCE,
    resource: resource,
    rootResource: rootResource,
    resourceIds: resourceIds,
    resourcePath: resourcePath,
    fieldPath: fieldPath,
  modifiedBreak: modifiedBreak
});

export const synchronizeResourceAction = (platform, resourceId) => ({
    type: Types.SYNCHRONIZE_RESOURCE,
    platform,
    resourceId
});

export const synchronizedResourceAction = (resource, platform, resourceId) => ({
    type: Types.SYNCHRONIZED_RESOURCE,
    resource,
    platform,
    resourceId,
    challengeId: resource.challengeId
});

export const deleteResourceAction = (type, resource, rootResource, resourceIds, resourcePath, fieldPath, data) => ({
    type: Types.DELETE_RESOURCE,
    resource: resource,
    resourceType: type,
    rootResource: rootResource,
    resourceIds: resourceIds,
    resourcePath: resourcePath,
    fieldPath: fieldPath,
    data: data
});

export const deletedResourceAction = (resource, rootResource, resourceIds, resourcePath, fieldPath, data, modifiedBreak) => ({
    type: Types.DELETED_RESOURCE,
    resource: resource,
    rootResource: rootResource,
    resourceIds: resourceIds,
    resourcePath: resourcePath,
    fieldPath: fieldPath,
    data: data,
  modifiedBreak: modifiedBreak
});

export const getResourcePageAction = (type, count, page, sortOptions, filterOptions, pageId, params) => ({
    type: Types.GET_RESOURCE_PAGE,
    pageId: pageId,
    resourceType: type,
    count: count,
    page: page,
    sortOptions: sortOptions,
    filterOptions: filterOptions,
    params: params || {}
});

export const receivedResourcePageAction = (type, resourcePage, sortOptions, filterOptions, pageId, params) => ({
    type: Types.RECEIVED_RESOURCE_PAGE,
    pageId: pageId,
    resourceType: type,
    resourcePage: resourcePage,
    sortOptions: sortOptions ? clone(sortOptions) : null,
    filterOptions: filterOptions ? clone(filterOptions) : null,
    params: params || {}
});

export const clearResourcePageAction = resourceType => ({
    type: Types.CLEAR_RESOURCE_PAGE,
    resourceType
});

export const getResourceAction = (type, id) => ({
    type: Types.GET_RESOURCE,
    resourceType: type,
    id: id
});

export const receivedResourceAction = (type, resource) => ({
    type: Types.RECEIVED_RESOURCE,
    resourceType: type,
    resource: resource
});

export const getAllResourcesAction = (type, sortOptions, filterOptions, params) => ({
    type: Types.GET_ALL_RESOURCES,
    resourceType: type,
    sortOptions: sortOptions,
    filterOptions: filterOptions,
    params: params || {}
});

export const receivedAllResourcesAction = (type, resources) => ({
    type: Types.RECEIVED_ALL_RESOURCES,
    resourceType: type,
    resources: resources
});

export const saveResourceFieldAction = (
    modifiedResourceId,
    rootResourceId,
    resourcePath,
    fieldPath,
    field,
    value,
    resourceParents,
    errors,
    sectionKeyPath,
    debounce,
    args,
    postActions = []
) => ({
    type: Types.SAVE_RESOURCE_FIELD,

    // handle the special case of retailerItemImage not having a parent resource at creation time.
    resourceType: get(modifiedResourceId, 'type', null),
    id: get(modifiedResourceId, 'id', null),

    field: field,
    value: value,
    rootResourceId: rootResourceId,
    resourcePath: resourcePath,
    fieldPath: fieldPath,
    resourceParents: resourceParents,
    errors: errors,
    sectionKeyPath: sectionKeyPath,
    debounce: isBoolean(debounce) ? debounce : true,
    args,
    postActions
});

export const sentSaveResourceFieldAction = (saveResourceFieldAction) => ({
    ...saveResourceFieldAction,
    type: Types.SENT_SAVE_RESOURCE_FIELD
});

// dispatch this action instead of calling the API directly
export const submitSaveResourceChangelist = (type, id, changelist, rootResourceId, resourcePath, parentIds, args) => ({
    type: Types.SAVE_RESOURCE_CHANGELIST_SUBMIT,
    resourceType: type,
    id,
    changelist,
    rootResourceId,
    resourcePath,
    parentIds,
    args
});

// does not call the API, just updates the store; good to use as an onSaveSuccess type of
// handler, or to update the Redux store with some transient application state that does
// not need to be persisted on the server.
export const saveResourceChangelistSuccess = (type, id, changelist, rootResourceId, resourcePath) => ({
    type: Types.SAVE_RESOURCE_CHANGELIST_SUCCESS,
    id: id,
    changelist: changelist,
    rootResourceId: rootResourceId,
    resourcePath: resourcePath
});

export const saveResourceChangelistError = (type, id, changelist, rootResourceId, resourcePath, error) => ({
    type: Types.SAVE_RESOURCE_CHANGELIST_ERROR,
    id: id,
    changelist: changelist,
    rootResourceId: rootResourceId,
    resourcePath: resourcePath,
    error: error
});

export const resourceFieldSavedAction = (saveFieldAction, data, modifiedBreak, resourceName = null) => ({
    type: FieldSavedResourceActionFor(resourceName),
    ...omit(saveFieldAction, ['type', 'additionalData']),
    saveData: data,
  modifiedBreak: modifiedBreak
});

export const saveResourceFieldErrorAction = (id, field, value, error, rootResourceId, fieldPath) => ({
    type: Types.SAVE_RESOURCE_FIELD_ERROR,
    id: id,
    field: field,
    value: value,
    error: error,
    rootResourceId: rootResourceId,
    fieldPath: fieldPath
});

export const updatePageNumberForResourceAction = (type, page, params) => ({
    type: Types.UPDATE_PAGE_NUMBER_FOR_RESOURCE,
    resourceType: type,
    page: page,
    params: params
});

export const updatePageCountForResourceAction = (type, count) => ({
    type: Types.UPDATE_PAGE_COUNT_FOR_RESOURCE,
    resourceType: type,
    count: count
});

export const toggleSortForResourceAction = (type, field) => ({
    type: Types.TOGGLE_SORT_FOR_RESOURCE,
    resourceType: type,
    field: field
});

export const addFilterForResourceAction = (type, name) => ({
    type: Types.ADD_FILTER_FOR_RESOURCE,
    resourceType: type,
    name: name
});

export const removeFilterForResourceAction = (type, name) => ({
    type: Types.REMOVE_FILTER_FOR_RESOURCE,
    resourceType: type,
    name: name
});

export const refreshPageAction = (resourceType, pageId) => ({
    type: Types.REFRESH_PAGE,
    resourceType: resourceType,
    pageId: pageId
});

export const clearSaveErrorsAction = () => ({
    type: Types.CLEAR_SAVE_ERRORS
});

export const getResourceInfo = (type) => ({
    type: Types.GET_RESOURCE_INFO,
    resourceType: type
});

export const receivedResourceInfo = (info) => ({
    type: Types.RECEIVED_RESOURCE_INFO,
    info: info
});



// WEBPACK FOOTER //
// ./public/resources/actions.js